import { Box, theme } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import AnswersAvailability from 'modules/surveys/components/Form/AnswersAvailability'
import NoQuestionsBanner from 'modules/surveys/components/Form/NoQuestionsBanner'
import PrivacySettingV1 from 'modules/surveys/components/Form/PrivacySettingV1'
import Stats from 'modules/surveys/components/Form/Stats'
import StatsHeader from 'modules/surveys/components/Form/Stats/Header'
import Supervisors from 'modules/surveys/components/Form/SupervisorsV1'
import { useRespondents } from 'modules/surveys/hooks/useRespondents'
import { SURVEY_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS } from 'modules/surveys/lib/constants'
import questionConfigs from 'modules/surveys/store/collections/questionConfigs'
import type SurveyForm from 'modules/surveys/store/models/Form'
import Survey from 'modules/surveys/store/models/v2/Survey'

import SurveyBasicInfoV1 from './SurveyBasicInfoV1'

const border = {
  width: 's1',
  style: 'solid',
  color: 'grey400',
} as const

interface Props {
  form: SurveyForm
  canEditSurveyForm: boolean
}

function SurveyConfiguration({ form: surveyForm, canEditSurveyForm }: Props) {
  const { navigation } = useNavigation()

  const surveyFormId = surveyForm.attributes.id
  const { loading: loadingRespondents, respondents } = useRespondents(surveyForm)
  const canBeAnonymous = respondents.length >= SURVEY_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS

  const { loading } = useAsyncData(async () => {
    if (!surveyFormId) return

    return Promise.all([
      questionConfigs.fetch({
        data: {
          surveys_form_id: surveyFormId,
        },
      }),
    ])
  }, [surveyFormId])

  const hasAtLeastOneQuestion = questionConfigs.models.length > 0

  if (loading || loadingRespondents) return <Loading />

  return (
    <>
      {surveyForm.isDraft && !hasAtLeastOneQuestion && (
        <Box
          style={{
            marginTop: `calc(${theme.dimensions.bodyPadding} * -1)`,
            marginBottom: theme.dimensions.bodyPadding,
          }}
        >
          <NoQuestionsBanner />
        </Box>
      )}
      <PanesPad>
        <Stats
          form={surveyForm}
          header={
            <StatsHeader
              editCopy={i18n.t('surveys.review.configuration.stats.edit_form')}
              reactivateCopy={i18n.t('surveys.review.configuration.stats.reactivate_survey')}
              form={surveyForm}
              startCopy={i18n.t('surveys.form.configuration.stats.start_form')}
              startRoute={navigation.processes.surveys.id.configuration.start.fullPath({
                id: surveyFormId,
              })}
              hasAtLeastOneQuestion={hasAtLeastOneQuestion}
              canEditSurveyForm={canEditSurveyForm}
            />
          }
        />
      </PanesPad>
      <SurveyBasicInfoV1
        form={new Survey(surveyForm.toJS())}
        canEditSurveyForm={canEditSurveyForm}
      />
      <Box
        border={{
          top: border,
        }}
      >
        <PrivacySettingV1
          form={surveyForm}
          canBeAnonymous={canBeAnonymous}
          canEditSurveyForm={canEditSurveyForm}
        />
      </Box>
      <Box
        border={{
          top: border,
        }}
      >
        <AnswersAvailability form={surveyForm} />
      </Box>
      <Box
        border={{
          top: border,
        }}
      >
        <Supervisors form={surveyForm} />
      </Box>
    </>
  )
}

export default observer(SurveyConfiguration)
